import { segment } from '@actions/analytics';
import { updatePartialUserDetails } from '@api/fetchers/auth';
import useGeoLocation from '@api/hooks/useGeoLocation';
import constants, {
  ERROR_CODE,
  LOGIN_OTP_SUBMITTED,
  OTP_INVALID
} from '@cont/Login/constants';
import segmentStorage from '@events/segmentStorage';
import useLoginStore from '@stores/login/loginStore';
import {
  getIsRegistered,
  getLoginAction,
  getLoginMethod,
  getOtpOrEmailResponse,
  getLoginResponse,
  getSetIsLoggingIn
} from '@stores/selectors/login';
import { useSnackbar } from '@stores/snackbar';
import { readCookie } from '@utils/cookies';
import validateRedirectURL from '@utils/validateRedirectUrl';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useLoginExperimentEventProps from '@hooks/segment/useLoginExperimentEventProps';
import getBackend from '../getBackend';
import processLoginUserData from '../processLoginData';
import handleUnregisteredUserStepPostLogin from '../handleUnregisteredUserStepPostLogin';
import useIsLoginTriggeredFromIframe from './useIsLoginTriggeredFromIframe';

const useSubmitLogin = ({
  otp,
  step,
  setStep,
  setOtpStepError,
  email,
  fieldsData,
  userInfo,
  source,
  sourceType,
  loginType,
  afterLogin,
  presetRedirectURL,
  country,
  mobileNumber,
  ttuUserName,
  password,
  selectedAgeGroup,
  accountType,
  handleSourceLogin
}) => {
  const { geoLocationData: { countryCode } = {} } = useGeoLocation();
  const loginMethod = useLoginStore(getLoginMethod);
  const setIsLoggingIn = useLoginStore(getSetIsLoggingIn);
  const goalSegmentData = useGoalEventProps({});
  const otpOrEmailResponse = useLoginStore(getOtpOrEmailResponse);
  const storedLoginResponse = useLoginStore(getLoginResponse);
  const isRegistered = useLoginStore(getIsRegistered);
  const login = useLoginStore(getLoginAction);
  const isLoginTriggeredFromIframe = useIsLoginTriggeredFromIframe();
  const { open: showSnackbar } = useSnackbar();
  const experimentData = useLoginExperimentEventProps(source);

  const handleLoginSubmitError = (response) => {
    if (response?.error?.error_code) {
      setOtpStepError({
        message: response?.error?.message,
        code: response?.error?.error_code
      });
      // sending the event in case of invalid otp
      if (response?.error?.error_code === ERROR_CODE.INVALID_OTP) {
        segment.track(OTP_INVALID, {
          ...goalSegmentData,
          is_new_user: !isRegistered,
          login_medium:
            loginType === constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile',
          login_primary_source: segmentStorage.getItem('lps'),
          redirect_to: validateRedirectURL(presetRedirectURL) || 'NA',
          proxy_id: `${country?.code}${mobileNumber}`
        });
      }
    } else {
      // fallback for unknown error codes
      const errorType =
        accountType === constants.ACCOUNT_TYPE_SIGNUP ? 'register' : 'login';
      showSnackbar({
        message:
          response.error?.message || `Unable to ${errorType}. Please try again`,
        type: 'error'
      });
    }
  };

  return async () => {
    setOtpStepError(null);
    const backend = getBackend({ loginType, loginMethod, otpOrEmailResponse });
    if (
      (step === constants.ENTER_OTP_STEP ||
        step === constants.ENTER_OTP_UNREGISTERED_USER_STEP) &&
      otpOrEmailResponse &&
      !sourceType
    ) {
      segment.track(LOGIN_OTP_SUBMITTED, {
        ...(experimentData || {}),
        is_new_user: !isRegistered,
        login_medium:
          loginType === constants.LOGIN_TYPE_EMAIL ? 'Email' : 'Mobile',
        login_primary_source: segmentStorage.getItem('lps'),
        redirect_to: validateRedirectURL(presetRedirectURL) || 'NA',
        proxy_id: `${country?.code}${mobileNumber}`
      });
    }

    const { firstName, lastName, data } = processLoginUserData({
      email,
      mobileNumber,
      isRegistered,
      fieldsData,
      loginType,
      sourceType,
      userInfo,
      source,
      ttuUserName
    });

    if (step === constants.UPDATE_PARTIAL_USER_DETAILS_STEP) {
      setIsLoggingIn(true);
      const response = await updatePartialUserDetails({
        // email: '',
        firstName,
        lastName,
        state: fieldsData.state.value,
        accessToken: readCookie('partialUserAccessToken')
      });
      setIsLoggingIn(false);
      if (response?.error) return handleLoginSubmitError(response);
      // Use the existing stored login response, and log the user in using the access and refresh token in it.
      afterLogin(storedLoginResponse, true);
    } else {
      const loginResponse = await login({
        email: data.email,
        phone: data.mobileNumber,
        otp,
        countryCode: country?.code || countryCode,
        backend,
        firstName,
        lastName,
        state: fieldsData.state.value,
        password,
        register: !isRegistered,
        ageGroupID: selectedAgeGroup?.id,
        accessToken: data.accessToken
      });
      if (
        sourceType === 'marketing' &&
        handleSourceLogin &&
        loginResponse?.error?.statusCode === 400
      )
        handleSourceLogin(loginResponse);
      if (loginResponse?.error) return handleLoginSubmitError(loginResponse);
      if (loginResponse?.access_token) {
        if (step === constants.ENTER_OTP_UNREGISTERED_USER_STEP) {
          handleUnregisteredUserStepPostLogin({
            country,
            loginResponse,
            mobileNumber,
            loginType,
            isLoginTriggeredFromIframe,
            setStep,
            experimentData
          });
          setIsLoggingIn(false);
        } else afterLogin(loginResponse);
      }
    }
  };
};

export default useSubmitLogin;
